import { useRef, useEffect, useCallback } from "react";
import axios from "axios";

/**
 * Check if an error is a cancellation (AbortError or axios CanceledError).
 */
const isCancelError = (err: any): boolean => {
  return axios.isCancel(err) || err?.name === "AbortError" || err?.name === "CanceledError";
};

/**
 * Hook that manages an AbortController for cancellable async operations.
 *
 * @example
 * const { createNewSignal, shouldIgnoreError } = useAbortController();
 *
 * const fetchData = async () => {
 *   const signal = createNewSignal();
 *   try {
 *     const response = await fetch('/api/data', { signal });
 *     if (signal.aborted) return;
 *     setData(await response.json());
 *   } catch (error) {
 *     if (shouldIgnoreError(error)) return;
 *     // Handle real errors
 *   }
 * };
 */
export const useAbortController = () => {
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Aborts any in-flight request and creates a fresh AbortController.
   * Returns the new signal to pass into fetch/axios calls.
   */
  const createNewSignal = useCallback((): AbortSignal => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    return abortControllerRef.current.signal;
  }, []);

  /**
   * Manually abort the current request.
   */
  const abort = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  /**
   * Returns true if the current controller's signal has been aborted.
   */
  const isAborted = useCallback(() => {
    return abortControllerRef.current?.signal?.aborted || false;
  }, []);

  /**
   * Single check for catch blocks: returns true if the error is a
   * cancellation (axios CanceledError, native AbortError) OR the
   * current signal is already aborted. When true, callers should
   * silently return without updating state or showing errors.
   */
  const shouldIgnoreError = useCallback((err: any): boolean => {
    return isCancelError(err) || (abortControllerRef.current?.signal?.aborted || false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    createNewSignal,
    abort,
    isAborted,
    shouldIgnoreError,
  };
};
